<template>
  <div>
    <!-- disabled button to show for unauthenticated/unauthorized users -->
    <template v-if="actionPermissions.block">
      <button
        id="saveToLibraryButton"
        class="btn btn-success btn-lg btn-block"
        @click="() => {showBlocker = true}">
        <i class="fa fa-save"/> Save to Library
      </button>
      <LimitActionModal :show.sync="showBlocker" :action-permissions="actionPermissions" />
    </template>

    <!-- button dropdown for regular, active users -->
    <A11yDropdown
      v-if="!actionPermissions.block"
      :dropdown-props="{
        ref:'dropdown',
        class:'library-dropdown btn-block'
      }"
      :button-props="{
        'aria-live':'polite',
        'data-role':'trigger',
        class:'btn btn-success btn-lg btn-block dropdown-toggle',
        disabled:saving,
        id:'saveToLibraryButton',
        'aria-labelledby':'save-to-library-label',
      }"
      :dom-attributes="{
        role:'menu',
      }"
    >
      <template slot="button-contents">
        <template v-if="!saving">
          <i class="fa fa-save"/>
          <span id="save-to-library-label" aria-hidden="true">Save to Library</span>
        </template>
        <template v-if="saving">
          <i class="fa fa-circle-o-notch fa-spin"/>
          <span>Saving...</span>
        </template>
        <span class="caret"/>
      </template>
      <template slot="dropdown-items">
        <li v-if="recentStudents.length > 0"><a role="button" @click="saveForStudent(recentStudents[0])">Save to <span class="fs-block">{{ recentStudents[0].name }}</span></a></li>
        <li><a role="button" @click="toggleStudentModal(true)">Save to {{ recentStudents.length > 0 && 'another' || 'a' }} folder</a></li>
      </template>
    </A11yDropdown>

    <div class="saved-count gb-text-1" v-if="libraryCount > 0">
      <span class="saved-count__count gb-text-3">{{ libraryCount }}</span> {{ libraryCount === 1 && 'version' || 'versions' }} of this page {{ libraryCount === 1 && 'is' || 'are' }} <br>saved in <router-link :to="{name: 'Library'}">Your Library</router-link>.
    </div>

    <!-- modal where user selects student to save content for -->
    <modal
      v-model="showStudentModal"
      title="Save to Library"
      @hide="choseStudent(null)">
      <transition name="fade">
        <ChooseStudentForm
          v-if="showStudentModal"
          :all-students="students"
          :recent-students="recentStudents"
          :preselected-student="student"
          @selectedStudent:existing="choseStudent"
          @selectedStudent:new="choseStudent"
        />
      </transition>

      <div v-if="enableNotes">
        <hr>
        <h5>
          Add Notes <span class="text-muted font-weight-normal">You can add/change this later</span>
        </h5>
        <textarea
          rows="3"
          class="form-control"
          placeholder="How would you implement this strategy?"
          v-model="notes"/>
      </div>

      <div class="btn-toolbar" slot="footer">
        <button
          class="btn btn-success btn-lg"
          :disabled="!studentName || saving"
          @click="save">
          <template v-if="!saving">Save to {{ studentName || 'folder' }}</template>
          <template v-if="saving">Saving...</template>
        </button>
        <button class="btn btn-link btn-lg" @click="toggleStudentModal(false)">Cancel</button>
      </div>
    </modal>
  </div>
</template>

<script>
import { libraryQuotasMixin } from '@/components/library/library_quotas_mixin'
import LimitActionModal from '@/components/LimitActionModal'
import ChooseStudentForm from '@/components/library/ChooseStudentForm'
import { mapActions, mapState } from 'vuex'
import { CONTENT_TYPES } from '@/util/access_visibility'
import {
  addGoalForStudent,
  addStrategyForStudent,
  getLibraryCountForGoal,
  getLibraryCountForStrategy,
  addStudent,
  postStudentContent
} from '@/api/library'
import { postPresentLevelStudent } from '@/api/presentlevels'
import { toolkitUrls } from '@/links'
import A11yDropdown from '@/components/A11yDropdown'

export default {
  name: 'SaveToLibraryButton',
  mixins: [libraryQuotasMixin],
  components: {
    A11yDropdown,
    LimitActionModal,
    ChooseStudentForm
  },
  data() {
    return {
      showStudentModal: false,
      fetchingStudents: false,
      saving: false,
      showAllStudents: false,
      showBlocker: false,
      student: null,
      libraryCount: 0,
      fetchingLibraryCount: false,
      libraryUrl: toolkitUrls.library,
      notes: ''
    }
  },
  props: {
    contentUuid: {
      type: String,
      default: ''
    },
    userUuid: {
      type: String,
      default: ''
    },
    actionPermissions: {
      type: Object,
      required: true
    },
    contentPayload: {
      type: Object,
      required: true
    },
    enableNotes: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    ...mapState('library', ['students', 'recentStudents']),
    studentName() {
      return (this.student && this.student.name) || ''
    },
    blockerText() {
      if (this.actionPermissions.blockUnauthenticated) {
        return 'You must be signed in to use this feature'
      }
      if (this.actionPermissions.blockExpired) {
        return 'You must have an active membership to use this feature'
      }
    },
    contentType() {
      return this.actionPermissions.contentType
    }
  },
  methods: {
    ...mapActions('library', ['fetchStudents', 'fetchRecentStudents']),
    toggleStudentModal(flag) {
      this.showStudentModal = flag
      this.notes = ''
    },
    choseStudent(student) {
      this.student = student
    },
    fetchLibraryCount() {
      if (this.userUuid && this.contentUuid && !this.fetchingLibraryCount) {
        if ([CONTENT_TYPES.GOAL, CONTENT_TYPES.SUPER_PAGE].indexOf(this.contentType) > -1) {
          getLibraryCountForGoal(this.userUuid, this.contentUuid).then(count => {
            this.libraryCount = count
            this.fetchingLibraryCount = false
          })
        } else if (this.contentType === CONTENT_TYPES.STRATEGY) {
          getLibraryCountForStrategy(this.userUuid, this.contentUuid).then(data => {
            this.libraryCount = data.length
            this.fetchingLibraryCount = false
          })
        }
      }
    },
    loadUserStudents() {
      if (!this.actionPermissions.block) {
        this.fetchingStudents = true
        this.fetchRecentStudents(this.userUuid)
          .then(() => {
            return this.fetchStudents(this.userUuid)
          })
          .then(() => {
            this.fetchingStudents = false
          })
          .catch(err => {
            console.error(`Error loading folders in SaveToLibraryButton: ${err}`)
          })
      }
    },
    saveForStudent(student) {
      this.student = student
      if (this.enableNotes) {
        this.toggleStudentModal(true)
      } else {
        this.save()
      }
    },
    save() {
      if (
        this.student &&
        this.student.uuid &&
        this.maxContentReachedForStudent(this.contentType, this.student.uuid)
      ) {
        this.sendMaxContentInFolderEvent(this.contentType, this.student.uuid)
        this.showStudentModal = false
        this.$emit('saved:error', this.maxContentTypeErrorMessage(this.contentType))
        return
      }
      this.saving = true
      this.$emit('saved:start')
      var promise
      if ([CONTENT_TYPES.GOAL, CONTENT_TYPES.SUPER_PAGE].indexOf(this.contentType) > -1) {
        promise = addGoalForStudent(this.student, this.contentPayload)
        // analytics tracking
        this.$sendEvent(
          'Clicked Save Goal to Library',
          {},
          {
            cu: this.contentUuid,
            ct: this.actionPermissions.contentTitle
          }
        )
      } else if ([CONTENT_TYPES.PRESENTLEVEL].indexOf(this.contentType) > -1) {
        var studentUuid = this.student.uuid
        let newStudent = !studentUuid
        if (newStudent) {
          promise = addStudent(this.student.name, this.userUuid).then(data => {
            studentUuid = data.uuid
          })
        } else {
          promise = new Promise(res => {
            res()
          })
        }

        promise = promise
          // postPresentLevelStudent must occur before postStudentContent
          // because when the save operation completes, this component emits an event
          // where the expected payload is the result of adding the content to a collection
          .then(() => {
            return postPresentLevelStudent({
              uuid: this.contentUuid,
              userUuid: this.userUuid,
              studentUuid: studentUuid,
              pronoun: null,
              enrolledGrade: null,
              subjects: null
            })
          })
          .then(() => {
            return postStudentContent(
              studentUuid,
              CONTENT_TYPES.PRESENTLEVEL,
              this.contentUuid,
              this.userUuid
            )
          })

        this.$sendEvent('Clicked Save Present Levels to Library', {
          plw_uuid: this.contentUuid,
          plw_new_student: newStudent,
          //Deprecated
          present_level_uuid: this.contentUuid
        })
      } else if (this.contentType === CONTENT_TYPES.STRATEGY) {
        promise = addStrategyForStudent({
          userUuid: this.userUuid,
          strategyUuid: this.contentUuid,
          studentUuid: this.student.uuid,
          studentName: this.student.name,
          notes: this.notes
        })
        this.$sendEvent(
          'Clicked Save Strategy to Library',
          {},
          {
            cu: this.contentUuid,
            ct: this.actionPermissions.contentTitle
          }
        )
      }

      if (promise) {
        promise
          .then(resp => {
            this.saving = false
            this.showStudentModal = false
            this.student = null
            this.fetchLibraryCount()
            this.loadUserStudents()
            this.$emit('saved:success', resp)
          })
          .catch(err => {
            this.saving = false
            this.showStudentModal = false
            this.$emit('saved:error', err.message)
          })
      }
    }
  },
  watch: {
    userUuid(newVal) {
      if (newVal) {
        this.loadUserStudents()
        this.fetchLibraryCount()
      }
    },
    actionPermissions(newPermissions, oldPermissions) {
      if (oldPermissions.block && !newPermissions.block) {
        this.loadUserStudents()
      }
    },
    contentUuid(newVal) {
      // reset content-specific data/state
      this.saving = false
      this.student = null
      this.showStudentModal = false
      this.libraryCount = 0
      this.fetchLibraryCount()
    }
  },
  mounted() {
    if (this.userUuid) {
      this.loadUserStudents()
      this.fetchLibraryCount()
    }
  }
}
</script>

<style lang="less">
@import '~@/variables.less';
#saveToLibraryButton {
  &:hover, &:focus {
    background-color: var(--green-button-hover);
  }
  &:focus {
    outline: solid 2px var(--green-button-hover);
    outline-offset: 2px;
  }
}

.library-dropdown {
  .dropdown-menu {
    width: 100%;
    li a {
      white-space: normal;
    }
  }
}
.saved-count {
  margin-top: 12px;
  text-align: center;
  color: @gray-light;
  .saved-count__count {
    font-weight: bold;
  }
}

.font-weight-normal {
  font-weight: normal;
}

.btn-block {
  min-width: fit-content;
}
</style>
